import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import {
	Box,
	Button,
	ButtonGroup,
	Grid2,
	Paper,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { GridApi } from "ag-grid-community";
import type { Moment } from "moment";
import { useCallback, useEffect, useState } from "react";

import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { LinesFilter } from "@/pages/delays/shared/components/filters/LinesFilter";
import { ATHENS_GRAY } from "@/shared/colors";
import { useShiftAssetId } from "@/shared/hooks/useShiftAssetId";
import { PRODUCT_AREA_ASSET_ID } from "@/shared/models/Product";
import { ReclassifyBundleModal } from "../components/ReclassifyBundleModal";
import { useQualityCheckRefresh } from "../hooks/useQualityCheckRefresh";
import {
	getSawQualityChecksReport,
	type SawQualityCheckRow,
} from "../repositories/sawQualityChecksReportRepository";
import {
	type CheckModalMode,
	SawQualityCheckModal,
} from "./components/SawQualityCheckModal";
import { SawQualityChecksReportTable } from "./components/SawQualityChecksReportTable";

const moment = getMomentTz();

type ViewMode = "shift" | "period";

export const SawQualityChecksReportPage = () => {
	const queryClient = useQueryClient();
	const [error, setError] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [viewMode, setViewMode] = useState<ViewMode>("shift");
	const [filterLine, setFilterLine] = useState<number | null>(null);
	const [periodStart, setPeriodStart] = useState<Moment | null>(() =>
		moment().subtract(6, "days").startOf("day"),
	);
	const [periodEnd, setPeriodEnd] = useState<Moment | null>(() =>
		moment().endOf("day"),
	);
	const [reclassifyTarget, setReclassifyTarget] =
		useState<SawQualityCheckRow | null>(null);
	const [checkModal, setCheckModal] = useState<{
		check: SawQualityCheckRow;
		mode: CheckModalMode;
	} | null>(null);
	const isPeriod = viewMode === "period";

	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	// Saws area owns no shift scheme: a picked line resolves its own, "All" falls
	// back to Plant.
	const shiftAssetId = useShiftAssetId(filterLine, PRODUCT_AREA_ASSET_ID.saw);

	// Re-init when the picked line changes scheme, keeping the production date so
	// the navigator doesn't jump back to now(). shiftInfo is excluded on purpose:
	// the effect mutates it.
	// biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
	useEffect(() => {
		if (!shiftAssetId) return;
		const currentDate = shiftInfo?.CurrentProductionDate ?? null;
		(async () => {
			await getShiftDataFromAPI(currentDate, 0, shiftAssetId);
		})();
	}, [shiftAssetId]);

	const shiftId = shiftInfo?.CurrentShiftId ?? null;
	const periodStartStr = periodStart?.toISOString() ?? null;
	const periodEndStr = periodEnd?.toISOString() ?? null;

	const { data: rows = [], isLoading } = useQuery<SawQualityCheckRow[], Error>({
		// Under the "sawQualityChecks" prefix so every check mutation elsewhere
		// refreshes this page too.
		queryKey: [
			"sawQualityChecks",
			"report",
			filterLine,
			isPeriod ? "period" : shiftId,
			isPeriod ? periodStartStr : null,
			isPeriod ? periodEndStr : null,
		],
		queryFn: () =>
			getSawQualityChecksReport({
				assetId: filterLine,
				shiftId: isPeriod ? null : shiftId,
				startDate: isPeriod ? periodStartStr : null,
				endDate: isPeriod ? periodEndStr : null,
			}),
		enabled: isPeriod ? !!(periodStart && periodEnd) : shiftId !== null,
		onError: (e: Error) => setError(e.message),
		meta: {
			realtime: ["FAB.GetSawQualityChecksReport", "FAB.GetSawQualityChecks"],
		},
	});

	const refresh = useCallback(
		() => queryClient.invalidateQueries({ queryKey: ["sawQualityChecks"] }),
		[queryClient],
	);

	// Stable: the grid registers its context-menu options once, on mount.
	const handleEdit = useCallback(
		(check: SawQualityCheckRow) => setCheckModal({ check, mode: "edit" }),
		[],
	);
	const handleDeleteRequest = useCallback(
		(check: SawQualityCheckRow) => setCheckModal({ check, mode: "delete" }),
		[],
	);

	const handleCheckChanged = useCallback(async () => {
		await refresh();
		await queryClient.invalidateQueries({ queryKey: ["sawRecentBundles"] });
	}, [refresh, queryClient]);

	const handleReclassified = useQualityCheckRefresh();

	const handleExportToExcel = useCallback(() => {
		if (!gridApi) {
			setError("Unable to export data. Please try again.");
			return;
		}
		const params = {
			skipHeader: false,
			skipFooters: true,
			skipGroups: true,
			fileName: "saw_quality_checks_report.xlsx",
		};
		gridApi.exportDataAsExcel(params);
	}, [gridApi]);

	return (
		<>
			<HelmetDexteel title="Saw Quality Checks Report" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				style={{ backgroundColor: ATHENS_GRAY }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Saw Quality Checks Report
					</Typography>
				</Grid2>

				<Grid2 size="auto">
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<Box display="flex" alignItems="center" gap={2}>
							<ButtonGroup size="small" color="primary">
								<Button
									variant={viewMode === "shift" ? "contained" : "outlined"}
									onClick={() => setViewMode("shift")}
									sx={{ textTransform: "none", px: 2 }}
								>
									Shift
								</Button>
								<Button
									variant={viewMode === "period" ? "contained" : "outlined"}
									onClick={() => setViewMode("period")}
									sx={{ textTransform: "none", px: 2 }}
								>
									Period
								</Button>
							</ButtonGroup>
							{isPeriod ? (
								<Box display="flex" alignItems="center" gap={1}>
									<DatePicker
										label="Start"
										value={periodStart}
										onChange={(v) =>
											setPeriodStart(v ? v.startOf("day") : null)
										}
										format="MM/DD/YYYY"
										maxDate={periodEnd ?? undefined}
										slotProps={{
											textField: { size: "small", sx: { width: 150 } },
										}}
									/>
									<DatePicker
										label="End"
										value={periodEnd}
										onChange={(v) => setPeriodEnd(v ? v.endOf("day") : null)}
										format="MM/DD/YYYY"
										minDate={periodStart ?? undefined}
										slotProps={{
											textField: { size: "small", sx: { width: 150 } },
										}}
									/>
								</Box>
							) : (
								<ShiftDayNavigatorControl
									preventInit
									withPaper={false}
									assetId={shiftAssetId ?? 1}
								/>
							)}
						</Box>
					</Paper>
				</Grid2>
				<Grid2 size={{ xs: "auto" }}>
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<LinesFilter
							value={filterLine}
							onChange={setFilterLine}
							areaAssetId={PRODUCT_AREA_ASSET_ID.saw}
						/>
					</Paper>
				</Grid2>
				<Grid2 size={{ xs: "grow" }} />
				<Grid2
					size={{ xs: "auto" }}
					sx={{ display: "flex", alignItems: "center" }}
				>
					<ExcelButton
						handleExportToExcel={handleExportToExcel}
						disabled={rows.length === 0}
					/>
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<SawQualityChecksReportTable
						isLoading={isLoading}
						rows={rows}
						setGridApi={setGridApi}
						onReclassify={setReclassifyTarget}
						onEdit={handleEdit}
						onDelete={handleDeleteRequest}
					/>
				</Grid2>
			</Grid2>

			<ReclassifyBundleModal
				open={reclassifyTarget != null}
				assetId={reclassifyTarget?.AssetId ?? null}
				bundleId={reclassifyTarget?.BundleId ?? null}
				onClose={() => setReclassifyTarget(null)}
				onReclassified={handleReclassified}
			/>

			<SawQualityCheckModal
				check={checkModal?.check ?? null}
				mode={checkModal?.mode ?? ""}
				onClose={(shouldRefresh) => {
					setCheckModal(null);
					if (shouldRefresh) void handleCheckChanged();
				}}
				onError={(e) => setError(e.message)}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
